import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

/**
 * Sorts an array of players based on a specified key and direction.
 * @param {string} sort - The key to sort by ('name_last', 'name_first', 'player_number', 'player_type.id').
 * @param {'asc' | 'desc'} direction - The sort direction.
 * @returns {function(Object, Object): number} A comparison function for Array.prototype.sort().
 */
export const playerSort = (sort, direction) => (a, b) => {
  const asc = direction === 'asc'

  if (sort === 'name_last') {
    const aLastName = a?.name_last || ''
    const bLastName = b?.name_last || ''
    if (aLastName < bLastName) {
      return asc ? -1 : 1
    } else if (aLastName > bLastName) {
      return asc ? 1 : -1
    }
    // If last names are equal, sort by first name
    const aFirstName = a?.name_first || ''
    const bFirstName = b?.name_first || ''
    if (aFirstName < bFirstName) {
      return asc ? -1 : 1
    } else if (aFirstName > bFirstName) {
      return asc ? 1 : -1
    }
    return 0
  }

  if (sort === 'name_first') {
    const aFirstName = a?.name_first || ''
    const bFirstName = b?.name_first || ''
    if (aFirstName < bFirstName) {
      return asc ? -1 : 1
    } else if (aFirstName > bFirstName) {
      return asc ? 1 : -1
    }
    // If first names are equal, sort by last name
    const aLastName = a?.name_last || ''
    const bLastName = b?.name_last || ''
    if (aLastName < bLastName) {
      return asc ? -1 : 1
    } else if (aLastName > bLastName) {
      return asc ? 1 : -1
    }
    return 0
  }

  if (sort === 'player_number') {
    // Treat null/undefined player numbers consistently, e.g., push them to the end
    const aNum = a?.player_number
    const bNum = b?.player_number
    const aIsNull = aNum === null || typeof aNum === 'undefined'
    const bIsNull = bNum === null || typeof bNum === 'undefined'

    if (aIsNull && bIsNull) return 0
    if (aIsNull) return 1 // Nulls go last
    if (bIsNull) return -1 // Nulls go last

    // Attempt numerical comparison first
    const numA = parseInt(aNum, 10)
    const numB = parseInt(bNum, 10)

    if (!isNaN(numA) && !isNaN(numB)) {
      if (numA < numB) return asc ? -1 : 1
      if (numA > numB) return asc ? 1 : -1
      return 0
    }

    // Fallback to string comparison if parsing fails or values aren't numbers
    const strA = String(aNum)
    const strB = String(bNum)
    if (strA < strB) return asc ? -1 : 1
    if (strA > strB) return asc ? 1 : -1
    return 0
  }

  if (sort === 'player_type.id') {
    const aTypeId = a?.player_type?.id
    const bTypeId = b?.player_type?.id
    const aIsNull = aTypeId === null || typeof aTypeId === 'undefined'
    const bIsNull = bTypeId === null || typeof bTypeId === 'undefined'

    if (aIsNull && bIsNull) return 0
    if (aIsNull) return 1 // Nulls go last
    if (bIsNull) return -1 // Nulls go last

    if (aTypeId < bTypeId) {
      return asc ? -1 : 1
    } else if (aTypeId > bTypeId) {
      return asc ? 1 : -1
    }
  }

  return 0
}

/**
 * Sorts an array of players based on their date of birth.
 * @param {Object} a - The first player object.
 * @param {Object} b - The second player object.
 * @param {'asc' | 'desc'} direction - The sort direction.
 * @returns {number} A comparison function for Array.prototype.sort().
 */
export const dateOfBirthSort = (a, b, direction) => {
  const dateA = a?.birth_date ? dayjs.utc(a.birth_date) : null
  const dateB = b?.birth_date ? dayjs.utc(b.birth_date) : null
  if (!dateA && !dateB) {
    return 0
  }
  if (!dateA) {
    return 1 // No date goes last
  }
  if (!dateB) {
    return -1 // No date goes last
  }
  return direction === 'asc'
    ? dateA.isBefore(dateB)
      ? -1
      : 1
    : dateA.isBefore(dateB)
    ? 1
    : -1
}

/**
 * Sorts an array of players based on their status.
 * @param {Object} a - The first player object.
 * @param {Object} b - The second player object.
 * @param {'asc' | 'desc'} direction - The sort direction.
 * @returns {number} A comparison function for Array.prototype.sort().
 */
export const statusSort = (a, b, direction) => {
  const aHasSuspension =
    Array.isArray(a?.suspensions) &&
    a.suspensions.filter((s) => s.is_active).length > 0
  const bHasSuspension =
    Array.isArray(b?.suspensions) &&
    b.suspensions.filter((s) => s.is_active).length > 0

  if (aHasSuspension && !bHasSuspension) {
    return direction === 'asc' ? -1 : 1
  }
  if (!aHasSuspension && bHasSuspension) {
    return direction === 'asc' ? 1 : -1
  }
  if (a?.is_injured && !b?.is_injured) {
    return direction === 'asc' ? -1 : 1
  }
  if (!a?.is_injured && b?.is_injured) {
    return direction === 'asc' ? 1 : -1
  }
  return 0
}

/**
 * Sorts an array of players based on whether they have signed a waiver.
 * @param {Object} a - The first player object.
 * @param {Object} b - The second player object.
 * @param {'asc' | 'desc'} direction - The sort direction.
 * @returns {number} A comparison function for Array.prototype.sort().
 */
export const signedWaiverSort = (a, b, direction) => {
  if (a?.is_signed_waiver === b?.is_signed_waiver) {
    return 0
  }
  return a?.is_signed_waiver
    ? direction === 'asc'
      ? 1
      : -1
    : direction === 'asc'
    ? -1
    : 1
}

/**
 * Sorts an array of players based on their invite status.
 * @param {Object} a - The first player object.
 * @param {Object} b - The second player object.
 * @param {'asc' | 'desc'} direction - The sort direction.
 * @returns {number} A comparison function for Array.prototype.sort().
 */
export const inviteStatusSort = (a, b, direction) => {
  const aStatus = a?.is_accepted
    ? 'accepted'
    : a?.is_invited
    ? 'invited'
    : 'none'
  const bStatus = b?.is_accepted
    ? 'accepted'
    : b?.is_invited
    ? 'invited'
    : 'none'

  if (aStatus === bStatus) {
    return 0
  }

  const order = ['none', 'invited', 'accepted']
  const aIndex = order.indexOf(aStatus)
  const bIndex = order.indexOf(bStatus)

  return direction === 'asc' ? aIndex - bIndex : bIndex - aIndex
}

/**
 * Sorts an array of players based on their date of birth.
 * @param {Object} a - The first player object.
 * @param {Object} b - The second player object.
 * @param {'asc' | 'desc'} direction - The sort direction.
 * @returns {number} A comparison function for Array.prototype.sort().
 */
export const dobSort = (a, b, direction) => {
  const dateA = a?.birth_date ? dayjs.utc(a.birth_date) : null
  const dateB = b?.birth_date ? dayjs.utc(b.birth_date) : null
  if (!dateA && !dateB) {
    return 0
  }
  if (!dateA) {
    return 1 // No date goes last
  }
  if (!dateB) {
    return -1 // No date goes last
  }
  return direction === 'asc'
    ? dateA.isBefore(dateB)
      ? -1
      : 1
    : dateA.isBefore(dateB)
    ? 1
    : -1
}

/**
 * Sorts an array of players based on their player status.
 * @param {Object} a - The first player object.
 * @param {Object} b - The second player object.
 * @param {'asc' | 'desc'} direction - The sort direction.
 * @returns {number} A comparison function for Array.prototype.sort().
 */
export const playerStatusSort = (a, b, direction) => {
  const aHasSuspension =
    Array.isArray(a?.suspensions) &&
    a.suspensions.filter((s) => s.is_active).length > 0
  const bHasSuspension =
    Array.isArray(b?.suspensions) &&
    b.suspensions.filter((s) => s.is_active).length > 0

  if (aHasSuspension && !bHasSuspension) {
    return direction === 'asc' ? -1 : 1
  }
  if (!aHasSuspension && bHasSuspension) {
    return direction === 'asc' ? 1 : -1
  }

  if (a?.is_injured && !b?.is_injured) {
    return direction === 'asc' ? -1 : 1
  }
  if (!a?.is_injured && b?.is_injured) {
    return direction === 'asc' ? 1 : -1
  }

  return 0
}
